import type { ReactNode } from "react";

import { CategoryBadge } from "@/components/scenario/CategoryBadge";
import { DifficultyBadge } from "@/components/scenario/DifficultyBadge";
import { Card } from "@/components/ui/Card";
import type { Scenario } from "@/types";

interface ScenarioCardProps {
  scenario: Scenario;
  children: ReactNode;
}

const POSITION_TAG_PREFIX = "position:";

const ROLE_CONFIG: Record<Scenario["category"], { label: string; description: string; color: string }> = {
  batting:      { label: "You're at bat",           description: "Make the right hitting decision",  color: "text-blue-700" },
  baserunning:  { label: "You're on the bases",     description: "Make the right baserunning call",  color: "text-purple-700" },
  fielding:     { label: "You're in the field",     description: "Make the right defensive play",    color: "text-green-700" },
  pitching:     { label: "You're on the mound",     description: "Make the right pitching decision", color: "text-red-700" },
  situational:  { label: "You're managing the game", description: "Make the right strategic call",   color: "text-yellow-700" },
};

export function ScenarioCard({ scenario, children }: ScenarioCardProps) {
  const role = ROLE_CONFIG[scenario.category];
  const visibleTags = scenario.tags.filter((t) => !t.startsWith(POSITION_TAG_PREFIX));

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <DifficultyBadge difficulty={scenario.difficulty} />
        <CategoryBadge category={scenario.category} />
      </div>

      <div className="mb-3 rounded-lg border border-navy-100 bg-navy-50 px-4 py-3">
        <p className={`text-sm font-bold ${role.color}`}>{role.label}</p>
        <p className="text-xs text-gray-500">{role.description}</p>
      </div>

      <h2 className="mb-2 text-lg font-bold text-navy-700">{scenario.title}</h2>

      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <p className="mb-2 text-sm text-gray-700">{scenario.situation}</p>
        <p className="text-sm font-semibold text-navy-600">
          {scenario.playerName} — {scenario.playerPosition}
        </p>
        {visibleTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-navy-100 px-2 py-0.5 text-xs font-medium text-navy-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">{children}</div>
    </Card>
  );
}
